import api from './api';

export type PaginatedResponse<T> = {
  data: T[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
};

const toInt = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

type PaginationOptions = {
  page?: number;
  perPage?: number;
};

function normalizePaginatedResponse<T>(raw: any, { page, perPage }: PaginationOptions = {}): PaginatedResponse<T> {
  const basePage = page ?? 1;
  const basePerPage = Number.isFinite(perPage) && perPage ? Number(perPage) : undefined;

  if (raw && typeof raw === 'object' && Array.isArray(raw.data)) {
    const items = raw.data as T[];
    const fallbackTotal = items.length;
    const fallbackPerPage =
      basePerPage !== undefined ? basePerPage : fallbackTotal || 25;
    const metaSource =
      (raw.meta && typeof raw.meta === 'object' ? raw.meta : raw) as Record<string, any>;

    return {
      data: items,
      meta: {
        current_page: toInt(metaSource?.current_page ?? metaSource?.currentPage, basePage),
        last_page: toInt(metaSource?.last_page ?? metaSource?.lastPage, basePage),
        per_page: toInt(metaSource?.per_page ?? metaSource?.perPage, fallbackPerPage),
        total: toInt(metaSource?.total, fallbackTotal),
      },
    };
  }

  if (Array.isArray(raw)) {
    const total = raw.length;
    const perPageValue =
      basePerPage !== undefined ? basePerPage : total || 25;
    return {
      data: raw as T[],
      meta: {
        current_page: basePage,
        last_page: basePage,
        per_page: perPageValue,
        total,
      },
    };
  }

  return {
    data: [],
    meta: {
      current_page: basePage,
      last_page: basePage,
      per_page: basePerPage ?? 25,
      total: basePerPage ?? 0,
    },
  };
}

export type AdminMaterialRecord = {
  id: number;
  title: string;
  description: string | null;
  file_name: string;
  file_url: string | null;
  families: string[];
  mime_type: string | null;
  file_size: number | null;
  published_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type AdminVideoRecord = {
  id: number;
  title: string;
  description: string | null;
  embed_html: string | null;
  external_url: string | null;
  families: string[];
  published_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export async function listAdminMaterials(
  params: PaginationOptions = {},
): Promise<PaginatedResponse<AdminMaterialRecord>> {
  const { page, perPage } = params;
  const query: Record<string, unknown> = {};
  if (typeof page === 'number' && page > 0) {
    query.page = page;
  }
  if (typeof perPage === 'number' && perPage > 0) {
    query.per_page = perPage;
  }

  const { data } = await api.get('admin/materials', { params: query });
  return normalizePaginatedResponse<AdminMaterialRecord>(data, params);
}

export async function createAdminMaterial(payload: {
  title: string;
  description?: string;
  families: string[];
  file: File;
}) {
  const formData = new FormData();
  formData.append('title', payload.title);
  if (payload.description) {
    formData.append('description', payload.description);
  }
  payload.families.forEach(family => {
    formData.append('families[]', family);
  });
  formData.append('file', payload.file);

  const { data } = await api.post('admin/materials', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return data as AdminMaterialRecord;
}

export async function deleteAdminMaterial(id: number | string) {
  const materialId = typeof id === 'string' ? id : String(id);
  await api.delete(`admin/materials/${materialId}`);
}

export async function listAdminVideos(
  params: PaginationOptions = {},
): Promise<PaginatedResponse<AdminVideoRecord>> {
  const { page, perPage } = params;
  const query: Record<string, unknown> = {};
  if (typeof page === 'number' && page > 0) {
    query.page = page;
  }
  if (typeof perPage === 'number' && perPage > 0) {
    query.per_page = perPage;
  }

  const { data } = await api.get('admin/videos', { params: query });
  return normalizePaginatedResponse<AdminVideoRecord>(data, params);
}

export async function createAdminVideo(payload: {
  title: string;
  description?: string;
  embedHtml?: string;
  externalUrl?: string;
  families: string[];
}) {
  const body = {
    title: payload.title,
    description: payload.description ?? null,
    embed_html: payload.embedHtml ?? null,
    external_url: payload.externalUrl ?? null,
    families: payload.families,
  };

  const { data } = await api.post('admin/videos', body);
  return data as AdminVideoRecord;
}

export async function deleteAdminVideo(id: number | string) {
  const videoId = typeof id === 'string' ? id : String(id);
  await api.delete(`admin/videos/${videoId}`);
}
